import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, Loader2, CheckCircle2 } from "lucide-react";
import { SERVICES, BRAND } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUDGETS = [
  "Under RM 5,000",
  "RM 5,000 – RM 15,000",
  "RM 15,000 – RM 50,000",
  "RM 50,000+",
  "Not sure yet",
];

const LOCATION_OPTS = BRAND.locations.map((l) => l.city).concat(["Remote / Other"]);

export default function Contact() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    location: "Kuala Lumpur",
    services: [],
    budget: BUDGETS[1],
    message: "",
  });

  const toggleService = (title) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(title)
        ? f.services.filter((s) => s !== title)
        : [...f.services, title],
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll be in touch within 24 hours.");
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(typeof msg === "string" ? msg : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-black border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left info */}
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF3B30] mb-4">
              — Say hello
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.9]">
              Tell us about
              <br />
              the <span className="text-[#FF3B30]">brief</span>.
            </h2>
            <p className="mt-6 text-white/60 text-base md:text-lg leading-relaxed max-w-md">
              Drop your details — we reply within one working day with ideas,
              references and next steps.
            </p>

            <div className="mt-12 space-y-5">
              <ContactLine
                icon={<Mail size={16} />}
                label="Email"
                value={BRAND.email}
                href={`mailto:${BRAND.email}`}
                testId="contact-email-link"
              />
              <ContactLine
                icon={<Phone size={16} />}
                label="Phone"
                value={BRAND.phone}
                href={`tel:${BRAND.phoneRaw}`}
                testId="contact-phone-link"
              />
              <ContactLine
                icon={<MessageCircle size={16} />}
                label="WhatsApp"
                value="Chat with us →"
                href={`https://wa.me/${BRAND.phoneRaw}?text=${encodeURIComponent(BRAND.whatsappMessage)}`}
                testId="contact-whatsapp-link"
              />
            </div>

            <div className="hline my-10" />

            <div className="grid grid-cols-3 gap-4 text-xs text-white/50">
              {BRAND.locations.map((l) => (
                <div key={l.city}>
                  <div className="text-white uppercase tracking-[0.2em] text-[11px] mb-2">
                    {l.city}
                  </div>
                  <div className="leading-relaxed">{l.coverage}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="contact-success"
                className="glass p-12 text-center h-full flex flex-col items-center justify-center"
              >
                <CheckCircle2 size={48} className="text-[#FF3B30]" />
                <h3 className="mt-6 font-display text-3xl md:text-4xl font-black uppercase">
                  Message received.
                </h3>
                <p className="mt-4 text-white/70 max-w-md">
                  Thanks {form.name?.split(" ")[0] || "friend"} — our team is on
                  it. Expect a reply within 24 hours.
                </p>
                <button
                  data-testid="contact-reset"
                  onClick={() => {
                    setDone(false);
                    setForm({
                      name: "",
                      email: "",
                      phone: "",
                      company: "",
                      location: "Kuala Lumpur",
                      services: [],
                      budget: BUDGETS[1],
                      message: "",
                    });
                  }}
                  className="btn-ghost mt-8"
                >
                  Send another
                </button>
              </motion.div>
            ) : (
              <motion.form
                onSubmit={onSubmit}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                data-testid="contact-form"
                className="glass p-6 md:p-10 flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <FormField label="Full name" testId="contact-name-field">
                    <input
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className={inputCls}
                      placeholder="Aisyah Tan"
                    />
                  </FormField>
                  <FormField label="Email" testId="contact-email-field">
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={inputCls}
                      placeholder="you@brand.com"
                    />
                  </FormField>
                  <FormField label="Phone" testId="contact-phone-field">
                    <input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className={inputCls}
                      placeholder="+60 12 345 6789"
                    />
                  </FormField>
                  <FormField label="Company" testId="contact-company-field">
                    <input
                      value={form.company}
                      onChange={(e) => setForm({ ...form, company: e.target.value })}
                      className={inputCls}
                      placeholder="Optional"
                    />
                  </FormField>
                  <FormField label="Location" testId="contact-location-field">
                    <select
                      value={form.location}
                      onChange={(e) => setForm({ ...form, location: e.target.value })}
                      className={inputCls}
                    >
                      {LOCATION_OPTS.map((l) => (
                        <option key={l} value={l} className="bg-[#111]">
                          {l}
                        </option>
                      ))}
                    </select>
                  </FormField>
                  <FormField label="Budget" testId="contact-budget-field">
                    <select
                      value={form.budget}
                      onChange={(e) => setForm({ ...form, budget: e.target.value })}
                      className={inputCls}
                    >
                      {BUDGETS.map((b) => (
                        <option key={b} value={b} className="bg-[#111]">
                          {b}
                        </option>
                      ))}
                    </select>
                  </FormField>
                </div>

                <FormField label="Services you're interested in" testId="contact-services-field">
                  <div className="flex flex-wrap gap-2">
                    {SERVICES.map((s) => {
                      const active = form.services.includes(s.title);
                      return (
                        <button
                          type="button"
                          key={s.id}
                          onClick={() => toggleService(s.title)}
                          data-testid={`contact-chip-${s.id}`}
                          className={`text-[11px] uppercase tracking-[0.2em] px-3 py-2 border transition-all ${
                            active
                              ? "bg-[#FF3B30] border-[#FF3B30] text-white"
                              : "border-white/10 text-white/60 hover:border-white/40 hover:text-white"
                          }`}
                        >
                          {s.title}
                        </button>
                      );
                    })}
                  </div>
                </FormField>

                <FormField label="Project brief" testId="contact-message-field">
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className={`${inputCls} resize-none`}
                    placeholder="Tell us about goals, audience, timeline…"
                  />
                </FormField>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                  <div className="text-xs text-white/40 max-w-md">
                    By submitting, you agree to be contacted by Empulse. We
                    never share your details.
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="btn-primary inline-flex items-center gap-3 disabled:opacity-60"
                  >
                    {submitting && <Loader2 size={16} className="animate-spin" />}
                    {submitting ? "Sending…" : "Send message"}
                  </button>
                </div>
              </motion.form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

const inputCls =
  "w-full bg-black/40 border border-white/10 px-4 py-3.5 text-sm text-white placeholder:text-white/30 focus:border-[#FF3B30] focus:outline-none transition-colors";

function FormField({ label, children, testId }) {
  return (
    <label className="flex flex-col gap-2" data-testid={testId}>
      <span className="text-[11px] uppercase tracking-[0.25em] text-white/50">
        {label}
      </span>
      {children}
    </label>
  );
}

function ContactLine({ icon, label, value, href, testId }) {
  return (
    <a
      href={href}
      target={href?.startsWith("http") ? "_blank" : undefined}
      rel="noreferrer"
      data-testid={testId}
      className="flex items-center gap-4 group"
    >
      <span className="w-10 h-10 border border-white/10 flex items-center justify-center group-hover:border-[#FF3B30] group-hover:text-[#FF3B30] transition-colors">
        {icon}
      </span>
      <span>
        <span className="block text-[11px] uppercase tracking-[0.25em] text-white/40">
          {label}
        </span>
        <span className="block text-white/90 group-hover:text-[#FF3B30] transition-colors">
          {value}
        </span>
      </span>
    </a>
  );
}
